<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { AdminDialog, RrButton, RrCard, RrTag } from '@xjlv2/admin-layout'
import { QrCode } from '@lucide/vue'
import type { ApiClient as ApiClientType } from '@xjlv2/api-client'

const props = defineProps<{
  createApiClient: () => ApiClientType
  wechatScanEnabled: boolean
}>()

const status = ref<{ bound: boolean; boundAt: string | null }>({ bound: false, boundAt: null })
const showBindDialog = ref(false)
const loading = ref(false)
const bindTicketId = ref<string | null>(null)
const bindQrBase64 = ref<string | null>(null)
const bindPollTimer = ref<number | null>(null)

onMounted(async () => {
  if (!props.wechatScanEnabled) return
  await loadStatus()
})

onUnmounted(() => {
  stopBindPolling()
})

async function loadStatus() {
  try {
    const result = await props.createApiClient().get<typeof status.value>('/api/saas/user/wechat/bind-status')
    status.value = result
  } catch { /* ignore — wechat binding status may not be available */ }
}

async function startBind() {
  loading.value = true
  try {
    const result = await props.createApiClient().post<{ ticketId: string; qrImageBase64: string; expiresAt: string }>(
      '/api/saas/user/wechat/bind-ticket', {})
    bindTicketId.value = result.ticketId
    bindQrBase64.value = result.qrImageBase64
    showBindDialog.value = true
    startBindPolling()
  } catch { /* ignore */ }
  loading.value = false
}

function startBindPolling() {
  stopBindPolling()
  bindPollTimer.value = window.setInterval(async () => {
    if (!bindTicketId.value) return
    try {
      const result = await props.createApiClient().get<{ status: string }>(
        `/api/saas/user/wechat/bind-ticket/${bindTicketId.value}/status`)
      if (result.status === 'CONFIRMED') {
        stopBindPolling()
        showBindDialog.value = false
        await loadStatus()
      }
    } catch {
      stopBindPolling()
    }
  }, 2000)
}

function stopBindPolling() {
  if (bindPollTimer.value !== null) {
    window.clearInterval(bindPollTimer.value)
    bindPollTimer.value = null
  }
}

function cancelBind() {
  stopBindPolling()
  showBindDialog.value = false
  bindTicketId.value = null
  bindQrBase64.value = null
}

async function unbind() {
  if (!confirm('确定解除微信绑定？解除后将无法使用微信扫码登录。')) return
  loading.value = true
  try {
    await props.createApiClient().delete('/api/saas/user/wechat/bind')
    await loadStatus()
  } catch { /* ignore */ }
  loading.value = false
}
</script>

<template>
  <section v-if="wechatScanEnabled" class="wechat-binding-section" aria-label="微信安全绑定">
    <RrCard title="微信快捷登录绑定" bordered>
      <template #extra>
        <RrTag :tone="status.bound ? 'success' : 'neutral'">
          {{ status.bound ? '已绑定' : '未绑定' }}
        </RrTag>
      </template>

      <div class="wechat-binding-content">
        <div class="wechat-binding-info">
          <div class="wechat-binding-icon" :class="{ 'is-bound': status.bound }" aria-hidden="true">
            <QrCode :size="20" />
          </div>
          <div class="wechat-binding-text">
            <p v-if="status.bound" class="wechat-status-desc">
              已成功绑定微信账号（绑定于 {{ status.boundAt ? new Date(status.boundAt).toLocaleDateString() : '近期' }}），可直接使用微信扫码免密登录工作台。
            </p>
            <p v-else class="wechat-status-desc">
              当前账号尚未绑定微信，绑定后可使用微信扫码快捷登录，提升工作台访问效率与安全性。
            </p>
          </div>
        </div>

        <div class="wechat-binding-actions">
          <RrButton
            v-if="status.bound"
            type="default" danger
            :loading="loading"
            @click="unbind"
          >
            解除绑定
          </RrButton>
          <RrButton
            v-else
            type="primary"
            :loading="loading"
            @click="startBind"
          >
            立即绑定微信
          </RrButton>
        </div>
      </div>

      <AdminDialog
        :open="showBindDialog"
        title="扫码绑定微信"
        description="请使用微信扫描下方二维码完成账号绑定授权"
        :width="420"
        :show-footer="false"
        @cancel="cancelBind"
      >
        <div class="wechat-dialog-body">
          <div class="wechat-qr-container">
            <img
              v-if="bindQrBase64"
              :src="'data:image/png;base64,' + bindQrBase64"
              alt="微信绑定二维码"
              class="bind-qr-img"
            />
          </div>
          <p class="bind-hint">二维码有效期为 5 分钟，扫码后请在手机端确认</p>
          <RrButton type="default" @click="cancelBind">取消操作</RrButton>
        </div>
      </AdminDialog>
    </RrCard>
  </section>
</template>

<style scoped>
.wechat-binding-section {
  margin-top: var(--rr-space-4);
  width: 100%;
}

.wechat-binding-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--rr-space-4);
  flex-wrap: wrap;
}

.wechat-binding-info {
  display: flex;
  align-items: center;
  gap: var(--rr-space-3);
  min-width: 0;
  flex: 1 1 320px;
}

.wechat-binding-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--rr-radius-control);
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
  border: 1px solid var(--rr-color-border);
  flex-shrink: 0;
}

.wechat-binding-icon.is-bound {
  background: var(--rr-color-success-soft);
  color: var(--rr-color-success);
  border-color: var(--rr-color-success-border);
}

.wechat-binding-text {
  min-width: 0;
}

.wechat-status-desc {
  margin: 0;
  font-size: var(--rr-font-size-base, 13px);
  color: var(--rr-color-text);
  line-height: 1.5;
}

.wechat-binding-actions {
  flex-shrink: 0;
}

.wechat-dialog-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--rr-space-3);
  padding: var(--rr-space-2) 0;
}

.wechat-qr-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--rr-space-3);
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-panel);
  background: var(--rr-color-surface);
}

.bind-qr-img {
  width: 200px;
  height: 200px;
  display: block;
}

.bind-hint {
  margin: 0;
  font-size: var(--rr-font-size-xs, 12px);
  color: var(--rr-color-text-muted);
}
</style>
